import axios from 'axios';

// Configuration without sensitive credentials
const API_CONFIG = {
    baseUrl: process.env.REACT_APP_ATHENA_BASE_URL || 'https://api.preview.platform.athenahealth.com',
    practiceId: process.env.REACT_APP_PRACTICE_ID || '17568',
    apiVersion: 'v1'
};

class AthenaService {
    constructor() {
        this.baseUrl = `${API_CONFIG.baseUrl}/${API_CONFIG.apiVersion}/${API_CONFIG.practiceId}`;
        console.log('AthenaService initialized with baseUrl:', this.baseUrl);
    }

    // No longer need to manage tokens in frontend
    // The server proxy handles all authentication

    async makeRequest(endpoint, method = 'GET', params = {}) {
        try {
            console.log(`Making ${method} request to ${endpoint}...`);
            
            const url = `${this.baseUrl}/${endpoint}`;
            let data = null;
            
            if (method === 'GET') {
                // For GET requests, params will be sent as query parameters
                const queryParams = new URLSearchParams();
                Object.entries(params).forEach(([key, value]) => {
                    queryParams.append(key, value);
                });
                const queryString = queryParams.toString();
                const urlWithParams = queryString ? `${url}?${queryString}` : url;
                
                console.log('Request details:', {
                    method,
                    url: urlWithParams,
                    params
                });
                
                // Use our secure server proxy - no auth headers needed
                const response = await axios.post('/api/athena-proxy', {
                    url: urlWithParams,
                    method
                });
                
                // Handle empty response
                if (!response?.data || Object.keys(response.data).length === 0) {
                    console.warn(`No data returned from ${endpoint}`);
                    return null; // Return null instead of throwing an error
                }
                
                console.log(`${endpoint} response:`, response.data);
                return response?.data;
            } else {
                // For POST, PUT, DELETE requests
                const formData = new URLSearchParams();
                Object.entries(params).forEach(([key, value]) => {
                    formData.append(key, value);
                });
                data = formData.toString();
                
                console.log('Request details:', {
                    method,
                    url,
                    data
                });
                
                // Use our secure server proxy - no auth headers needed
                const response = await axios.post('/api/athena-proxy', {
                    url,
                    method,
                    data
                });
                
                // Handle empty response
                if (!response?.data || Object.keys(response.data).length === 0) {
                    console.warn(`No data returned from ${endpoint}`);
                    return null; // Return null instead of throwing an error
                }
                
                console.log(`${endpoint} response:`, response.data);
                return response?.data;
            }
        } catch (error) {
            console.error(`${endpoint} request failed:`, {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                error: error.message,
                url: error.config?.url
            });
            throw error;
        }
    }

    async getDepartments() {
        try {
            console.log('Getting departments...');
            const response = await this.makeRequest('departments', 'GET', {
                showalldepartments: true,
                limit: 200,
                providerlist: true
            });
            console.log('Departments response:', response);
            const departments = response.departments || [];
            if (departments.length === 0) {
                console.warn('No departments found in response');
            }
            return departments;
        } catch (error) {
            console.error('getDepartments error:', error);
            throw error;
        }
    }

    async getProviders(departmentId) {
        try {
            console.log('Getting providers for department:', departmentId);
            const response = await this.makeRequest('providers', 'GET', {
                departmentid: departmentId,
                showallproviders: true,
                limit: 100
            });
            console.log('Providers response:', response);
            return response.providers || [];
        } catch (error) {
            console.error('getProviders error:', error);
            throw error;
        }
    }

    async getAppointmentTypes(departmentId, providerId) {
        try {
            console.log('Getting appointment types for department:', departmentId, 'and provider:', providerId);
            const response = await this.makeRequest(`appointmenttypes`, 'GET', {
                departmentid: departmentId,
                providerid: providerId,
                showdeleted: false,
                showfulldetails: true,
                limit: 100
            });
            console.log('Appointment types response:', response);
            return response.appointmenttypes || [];
        } catch (error) {
            console.error('getAppointmentTypes error:', error);
            throw new Error('Failed to get appointment types: ' + error.message);
        }
    }

    async testConnections() {
        const results = [];
        
        try {
            console.log('Testing Practice Info...');
            const practiceData = await this.makeRequest('practiceinfo', 'GET');
            console.log('Practice Info test successful');
            results.push({ endpoint: 'Practice Info', status: 'SUCCESS', data: practiceData });
        } catch (error) {
            console.log('Practice Info test failed:', error.response?.data || error.message);
            results.push({ 
                endpoint: 'Practice Info', 
                status: 'FAILED', 
                error: error.response?.data || error.message 
            });
        }

        // Check for existing departments
        try {
            console.log('\nChecking Departments...');
            const departmentsResponse = await this.makeRequest('departments', 'GET', {
                limit: 100,
                providerlist: true
            });
            const departments = departmentsResponse.departments;
            console.log(`Found ${departments.length} departments`);
            
            if (departments.length > 0) {
                // Find a department that's an office
                const officeDepartment = departments.find(d => d.facilitytypename === 'OFFICE');
                if (officeDepartment) {
                    console.log(`\nTesting Appointments for office department ${officeDepartment.facilityid} (${officeDepartment.facilityname})...`);
                    
                    const today = new Date();
                    const nextWeek = new Date(today);
                    nextWeek.setDate(today.getDate() + 7);

                    // First get appointment types
                    console.log('\nGetting appointment types...');
                    const appointmentTypesResponse = await this.makeRequest('configuration/appointmenttypes', 'GET', {
                        departmentid: officeDepartment.facilityid,
                        providerlist: 'true',
                        showalltypes: 'true'
                    });
                    const appointmentTypes = appointmentTypesResponse;
                    console.log(`Found ${appointmentTypes.length} appointment types:`, JSON.stringify(appointmentTypes, null, 2));

                    if (appointmentTypes.length > 0) {
                        const firstType = appointmentTypes[0];
                        console.log(`\nChecking open slots for appointment type: ${firstType.name}...`);
                        
                        // Get open appointment slots
                        const openSlotsResponse = await this.makeRequest('appointments/open', 'GET', {
                            departmentid: officeDepartment.facilityid,
                            appointmenttypeid: firstType.appointmenttypeid,
                            startdate: this.formatDate(today),
                            enddate: this.formatDate(nextWeek),
                            limit: 5,
                            // showfrozenslots: true,
                            ignoreschedulablepermission: true
                        });
                        const openSlots = openSlotsResponse.appointments || [];
                        console.log(`Found ${openSlots.length} open slots:`, JSON.stringify(openSlots, null, 2));

                        if (openSlots.length > 0) {
                            // First find a test patient
                            console.log('\nFinding or creating test patient...');
                            let testPatient;
                            
                            // Try to find existing patient
                            const patients = await this.makeRequest('patients/enhancedbestmatch', 'GET', {
                                firstname: 'Test',
                                lastname: 'Patient',
                                dob: '01/01/1990'
                            });

                            if (patients && patients.length > 0) {
                                testPatient = patients[0];
                                console.log('Found existing test patient:', testPatient);
                            } else {
                                // Create a new test patient
                                console.log('Creating new test patient...');
                                testPatient = await this.makeRequest('patients', 'POST', {
                                    departmentid: 1,
                                    firstname: 'Test',
                                    lastname: 'Patient',
                                    dob: '01/01/1990',
                                    address1: '123 Test St',
                                    city: 'TEST CITY',
                                    countrycode3166: 'US',
                                    state: 'CA',
                                    zip: '12345',
                                    guarantoraddresssameaspatient: true,
                                    portalaccessgiven: true,
                                    privacyinformationverified: true
                                });
                                console.log('Created new test patient:', testPatient);
                            }

                            // Try to book each slot until one succeeds
                            let appointmentBooked = false;
                            let lastError = null;
                            let successfulAppointment = null;

                            for (const slot of openSlots) {
                                try {
                                    console.log(`\nAttempting to book appointment for slot: ${slot.date} ${slot.starttime}...`);
                                    const newAppointment = await this.makeRequest(`appointments/${slot.appointmentid}`, 'PUT', {
                                        patientid: parseInt(testPatient.patientid),
                                        appointmenttypeid: slot.appointmenttypeid,
                                        ignoreschedulablepermission: true
                                    });
                                    console.log('Successfully created appointment:', newAppointment);
                                    appointmentBooked = true;
                                    successfulAppointment = newAppointment;
                                    break;
                                } catch (error) {
                                    console.log(`Failed to book slot ${slot.date} ${slot.starttime}:`, error.response?.data || error);
                                    lastError = error;
                                }
                            }

                            if (!appointmentBooked) {
                                throw new Error(lastError.response?.data?.error || lastError.message);
                            }

                            results.push({ 
                                endpoint: 'Create Appointment', 
                                status: 'SUCCESS', 
                                data: successfulAppointment 
                            });
                        }
                    }
                } else {
                    console.log('No office departments found');
                }
            } else {
                console.log('No departments found');
            }
        } catch (error) {
            console.log('API test failed:', error.response?.data || error.message);
            results.push({ 
                endpoint: error.config?.url?.includes('departments') ? 'Departments' : 'Appointments', 
                status: 'FAILED', 
                error: error.response?.data || error.message 
            });
        }

        return results;
    }

    async testSchedulingEndpoints() {
        const results = [];

        try {
            // Test practice info
            console.log('\nTesting Practice Info...');
            const practiceInfo = await this.makeRequest('practiceinfo', 'GET');
            console.log('Practice Info test successful');
            results.push({ endpoint: 'Practice Info', status: 'SUCCESS', data: practiceInfo });

            // Get departments
            console.log('\nChecking Departments...');
            const departments = await this.makeRequest('departments', 'GET', {
                limit: 100,
                providerlist: true
            });
            console.log(`Found ${departments.departments.length} departments`);

            // Find the first office department
            const officeDepartment = departments.departments.find(d => d.facilitytypename === 'OFFICE');

            // Test appointments for that department
            if (officeDepartment) {
                console.log('\nTesting Appointments for office department', officeDepartment.facilityid, `(${officeDepartment.facilityname})...`);

                // First get appointment types
                console.log('\nGetting appointment types...');
                const appointmentTypes = await this.makeRequest('configuration/appointmenttypes', 'GET', {
                    departmentid: officeDepartment.facilityid,
                    providerid: '',
                    showdeleted: false,
                    showhidden: false
                });
                console.log('Found', appointmentTypes.length, 'appointment types:', appointmentTypes);

                // Get first appointment type
                const firstType = appointmentTypes[0];

                // Get open slots for the first appointment type
                console.log(`\nChecking open slots for appointment type: ${firstType.name}...`);
                const openSlots = await this.makeRequest('appointments/open', 'GET', {
                    departmentid: officeDepartment.facilityid,
                    appointmenttypeid: appointmentTypes[0].appointmenttypeid,
                    startdate: '03/01/2025',
                    enddate: '03/31/2025',
                    ignoreschedulablepermission: true,
                    limit: 5
                });

                // Filter out frozen slots
                const availableSlots = openSlots.filter(slot => !slot.frozen);
                console.log(`Found ${availableSlots.length} available slots:`, JSON.stringify(availableSlots, null, 2));

                if (availableSlots.length > 0) {
                    // First find a test patient
                    console.log('\nFinding or creating test patient...');
                    let testPatient;
                    
                    // Try to find existing patient
                    const patients = await this.makeRequest('patients/enhancedbestmatch', 'GET', {
                        firstname: 'Test',
                        lastname: 'Patient',
                        dob: '01/01/1990'
                    });

                    if (patients && patients.length > 0) {
                        testPatient = patients[0];
                        console.log('Found existing test patient:', testPatient);
                    } else {
                        // Create a new test patient
                        console.log('Creating new test patient...');
                        testPatient = await this.makeRequest('patients', 'POST', {
                            departmentid: 1,
                            firstname: 'Test',
                            lastname: 'Patient',
                            dob: '01/01/1990',
                            address1: '123 Test St',
                            city: 'TEST CITY',
                            countrycode3166: 'US',
                            state: 'CA',
                            zip: '12345',
                            guarantoraddresssameaspatient: true,
                            portalaccessgiven: true,
                            privacyinformationverified: true
                        });
                        console.log('Created new test patient:', testPatient);
                    }

                    // Try to book each slot until one succeeds
                    let appointmentBooked = false;
                    let lastError = null;
                    let successfulAppointment = null;

                    for (const slot of availableSlots) {
                        try {
                            console.log(`\nAttempting to book appointment for slot: ${slot.date} ${slot.starttime}...`);
                            const newAppointment = await this.makeRequest(`appointments/${slot.appointmentid}`, 'PUT', {
                                patientid: parseInt(testPatient.patientid),
                                appointmenttypeid: slot.appointmenttypeid,
                                ignoreschedulablepermission: true
                            });
                            console.log('Successfully created appointment:', newAppointment);
                            appointmentBooked = true;
                            successfulAppointment = newAppointment;
                            break;
                        } catch (error) {
                            console.log(`Failed to book slot ${slot.date} ${slot.starttime}:`, error.response?.data || error);
                            lastError = error;
                        }
                    }

                    if (!appointmentBooked) {
                        throw new Error(lastError.response?.data?.error || lastError.message);
                    }

                    results.push({ 
                        endpoint: 'Create Appointment', 
                        status: 'SUCCESS', 
                        data: successfulAppointment 
                    });
                }
            }
        } catch (error) {
            console.log('API test failed:', error.response?.data || error.message);
            results.push({ 
                endpoint: error.config?.url?.includes('departments') ? 'Departments' : 'Appointments', 
                status: 'FAILED', 
                error: error.response?.data || error.message 
            });
        }

        return results;
    }

    async getPracticeInfo() {
        try {
            const response = await this.makeRequest('practiceinfo', 'GET');
            return response;
        } catch (error) {
            throw new Error('Failed to get practice info: ' + (error.response?.data?.error || error.message));
        }
    }

    async getAppointmentTypes(departmentId, providerId) {
        try {
            console.log('Getting appointment types for department:', departmentId, 'and provider:', providerId);
            const response = await this.makeRequest('appointmenttypes', 'GET', {
                departmentid: departmentId,
                providerid: providerId,
                showalltypes: true
            });
            return response;
        } catch (error) {
            throw new Error('Failed to get appointment types: ' + (error.response?.data?.error || error.message));
        }
    }

    async getOpenSlots(params) {
        try {
            const response = await this.makeRequest('appointments/open', 'GET', params);
            return response;
        } catch (error) {
            throw new Error('Failed to get open slots: ' + (error.response?.data?.error || error.message));
        }
    }

    async findOrCreateTestPatient({ firstname, lastname, dob, email, mobilephone, departmentid }) {
        try {
            const formatedDob = this.formatDate(new Date(dob));
            // Try to find existing patient
            const patients = await this.makeRequest('patients/enhancedbestmatch', 'GET', {
                firstname,
                lastname,
                dob :formatedDob
            });

            if (patients && patients.length > 0) {
                return patients[0];
            }

            // Create a new test patient
            const newPatient = await this.makeRequest('patients', 'POST', {
                departmentid,
                firstname,
                lastname,
                dob : formatedDob,
                email, 
                mobilephone
            });

            return newPatient;
        } catch (error) {
            throw new Error('Failed to find or create test patient: ' + (error.response?.data?.error || error.message));
        }
    }

    async bookAppointment({ appointmentid, patientid, appointmenttypeid, insuranceDetails, notes }) {
        try {
            // Validate appointment type ID
            if (!appointmenttypeid || isNaN(appointmenttypeid)) {
                console.error('❌ Invalid appointment type ID:', appointmenttypeid);
                throw new Error(`Invalid appointment type ID: ${appointmenttypeid}. Must be a valid number.`);
            }

            // Build request parameters
            const requestParams = {
                patientid: parseInt(patientid),
                appointmenttypeid: parseInt(appointmenttypeid),
                ignoreschedulablepermission: true
            };
            
            // Add notes if provided
            if (notes) {
                requestParams.bookingnote = notes;
                console.log('📝 Added booking note:', notes);
            }
            
            // Add insurance information if provided
            if (insuranceDetails) {
                console.log('🏥 Processing insurance details:', {
                    hasInsurance: insuranceDetails.insurance,
                    hasMember: insuranceDetails.insuranceMember,
                    hasGroup: insuranceDetails.insuranceGroup
                });
                // Create insurance info object with all required fields
                const insuranceInfo = {
                    insurancecompany: insuranceDetails.insurance || '',
                    insuranceidnumber: insuranceDetails.insuranceMember || '',
                    insurancegroupid: insuranceDetails.insuranceGroup || '',
                    insurancenote: insuranceDetails.notes || '',
                    insurancephone: insuranceDetails.insurancePhone || '',
                    insuranceplanname: insuranceDetails.insurancePlan || '',
                    insurancepolicyholder: insuranceDetails.policyHolder || '',
                    patientrelationshiptopolicyholder: insuranceDetails.relationship || ''
                };
                
                // Convert insurance info to JSON string as required by the API
                requestParams.insuranceinfo = JSON.stringify(insuranceInfo);
                console.log('🏥 Insurance info added to request');
            }
            
            console.log('🎯 BOOKING APPOINTMENT - Input Parameters:', {
                appointmentid,
                patientid,
                appointmenttypeid,
                hasInsurance: !!insuranceDetails,
                hasNotes: !!notes
            });

            console.log('📋 Appointment Type ID Details:', {
                original: appointmenttypeid,
                parsed: parseInt(appointmenttypeid),
                type: typeof appointmenttypeid,
                isValid: !isNaN(parseInt(appointmenttypeid))
            });
            
            console.log('🚀 Final booking request parameters:', requestParams);
            console.log('📞 Making API call to book appointment:', appointmentid);
            const response = await this.makeRequest(`appointments/${appointmentid}`, 'PUT', requestParams);
            
            console.log('✅ Appointment booking successful:', {
                appointmentid,
                appointmenttypeid: requestParams.appointmenttypeid,
                patientid: requestParams.patientid,
                responseKeys: Object.keys(response || {})
            });
            
            return response;
        } catch (error) {
            console.error('❌ APPOINTMENT BOOKING FAILED:', {
                appointmentid,
                appointmenttypeid,
                patientid,
                error: error.message,
                responseData: error.response?.data
            });
            throw new Error('Failed to book appointment: ' + (error.response?.data?.error || error.message));
        }
    }

    async getBookedAppointments(options = {}) {
        try {
            console.log('Getting booked appointments with options:', JSON.stringify(options));
            
            // Format dates if provided
            let startDate = options.startDate;
            let endDate = options.endDate;
            
            if (startDate && !startDate.includes('/')) {
                startDate = this.formatDate(new Date(startDate));
            }
            
            if (endDate && !endDate.includes('/')) {
                endDate = this.formatDate(new Date(endDate));
            }
            
            const today = new Date();
            const pastDate = new Date(today);
            pastDate.setDate(today.getDate() - 15);
            const futureDate = new Date(today);
            futureDate.setDate(today.getDate() + 30);
            
            // Build parameters for the API request
            const params = {
                scheduledstartdate: startDate || this.formatDate(pastDate),
                scheduledenddate: endDate || this.formatDate(futureDate),
                limit: options.limit || 1000,
                showpatientdetail: true,
                showinsurance:true,
                showcancelled:true
            };
            
            // Support both camelCase and lowercase parameter names
            
            // Handle departmentId/departmentid - ensure it's an integer
            if (options.departmentid) {
                params.departmentid = parseInt(options.departmentid, 10);
            } else if (options.departmentId) {
                params.departmentid = parseInt(options.departmentId, 10);
            }
            
            // Handle providerId/providerid as an integer
            if (options.providerid) {
                params.providerid = Array.isArray(options.providerid) 
                    ? options.providerid[0] 
                    : options.providerid;
            } else if (options.providerId) {
                params.providerid = Array.isArray(options.providerId) 
                    ? options.providerId[0] 
                    : options.providerId;
            }
            
            // Handle providerIds as an array of integers
            if (options.providerids && Array.isArray(options.providerids) && options.providerids.length > 0) {
                params.providerids = options.providerids
                    .map(id => parseInt(id, 10))
                    .join(',');
            } else if (options.providerIds && Array.isArray(options.providerIds) && options.providerIds.length > 0) {
                params.providerids = options.providerIds
                    .map(id => parseInt(id, 10))
                    .join(',');
            }
            
            // If neither is provided, default to departmentid=1
            if (!params.departmentid && !params.providerid && !params.providerids) {
                params.departmentid = 1;
            }
            
            // Handle other parameters
            if (options.patientid) params.patientid = parseInt(options.patientid, 10);
            else if (options.patientId) params.patientid = parseInt(options.patientId, 10);
            
            if (options.appointmenttypeid) params.appointmenttypeid = parseInt(options.appointmenttypeid, 10);
            else if (options.appointmentTypeId) params.appointmenttypeid = parseInt(options.appointmentTypeId, 10);
            
            console.log('Request parameters:', JSON.stringify(params));
            
            const response = await this.makeRequest('appointments/booked', 'GET', params);
            
            if (!response || !response.appointments) {
                console.warn("No booked appointments found");
                return []; // Return empty array instead of throwing an error
            }
            
            console.log(`Retrieved ${response.appointments.length} booked appointments of ${response.totalcount} total`);
            return response.appointments || [];
        } catch (error) {
            console.error('getBookedAppointments error:', error);
            console.error('Error details:', {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });
            
            // Fallback to local storage if available
            try {
                const localAppointments = JSON.parse(localStorage.getItem('appointments') || '[]');
                if (localAppointments.length > 0) {
                    console.log('Falling back to localStorage appointments');
                    return localAppointments;
                }
            } catch (localError) {
                console.error('LocalStorage fallback failed:', localError);
            }
            
            throw new Error('Failed to get booked appointments: ' + (error.response?.data?.error || error.message));
        }
    }

    async getAppointmentPatients (ids) {
        // Check if ids is an array or a Set
        const appointmentIds = Array.isArray(ids) ? ids : (ids instanceof Set ? Array.from(ids) : []);

        if (!appointmentIds || appointmentIds.length === 0) {
            console.warn('No appointment ids provided to getAppointmentPatients');
            // Return empty data structure instead of throwing an error
            return { appointments: [] };
        }
        console.log('Getting appointments for IDs:', appointmentIds);

        try {
            const response = await this.makeRequest('appointments/booked/multiple', 'GET', {
                appointmentids: appointmentIds.join(','),
                showpatientdetail: true,
                showclaimdetail: false,
                showinsurance: true
            });
            return response;
        }
        catch (error) { 
            console.error('Failed to get appointment patients:', error);
            // Return empty data structure instead of throwing
            return { appointments: [] };
        }
    }


    async getAppointmentReasonforNewPatient(departmentId, providerId) {
        try {
            console.log('Getting new appointment reasons for department:', departmentId, 'and provider:', providerId);
            const response = await this.makeRequest('patientappointmentreasons/newpatient', 'GET', {
                departmentid: departmentId,
                providerid: providerId
            })
            if (!response || Object.keys(response).length === 0) {
                console.warn("No new patient reasons found.");
                return [];
            }
            return response
        } catch (error) {
            throw new Error('Failed to get Appointment Reason for New Patient: ' + (error.response?.data?.error || error.message));
        }
    }

    async getAppointmentReasonforExistingPatient(departmentId, providerId) {
        try {
            console.log('Getting existing appointment reasons for department:', departmentId, 'and provider:', providerId);
            const response = await this.makeRequest('patientappointmentreasons/existingpatient', 'GET', {
                departmentid: departmentId,
                providerid: providerId
            })
            if (!response || Object.keys(response).length === 0) {
                console.warn("No existing patient reasons found.");
                return [];
            }
            return response
        } catch (error) {
            throw new Error('Failed to get Appointment Reason for Existing Patient: ' + (error.response?.data?.error || error.message));
        }
    }

    async getAvailableSlots(departmentId, appointmentType, providerId, startDate, endDate) {
        console.log(appointmentType);
        try {
            console.log('Getting available slots:', {
                departmentId,
                providerId,
                startDate,
                endDate
            });

            // Ensure date format is consistent with what's used when creating slots (MM/DD/YYYY)
            // If startDate/endDate aren't already in this format, format them
            const formattedStartDate = typeof startDate === 'string' && startDate.includes('/') 
                ? startDate 
                : this.formatDate(new Date(startDate));
                
            const formattedEndDate = endDate ? (typeof endDate === 'string' && endDate.includes('/') 
                ? endDate 
                : this.formatDate(new Date(endDate)))
                : formattedStartDate; // If no end date, use start date

            // Add the required parameters for the API - making sure to match exactly what's used for creation
            const response = await this.makeRequest('appointments/open', 'GET', {
                departmentid: departmentId,
                providerid: providerId,
                appointmenttypeid: appointmentType.appointmenttypeid,
                appointmenttype: appointmentType.name,	
                startdate: formattedStartDate,
                enddate: formattedEndDate,
                bypassscheduletimechecks: true, // Correct parameter name per API docs
                ignoreschedulablepermission: false,
                showfullresponse: true,
                showallproviders: true,
                showallresources: true,
                limit: 100 // Add limit to ensure we get enough results
            });

            console.log('Full API Response:', response);
            if (!response.appointments || response.appointments.length === 0) {
                console.log('No slots found. Trying alternate endpoint...');
                
                // Try alternate endpoint format
                const altResponse = await this.makeRequest('appointments/open/customschedule', 'GET', {
                    departmentid: departmentId,
                    providerid: providerId,
                    startdate: formattedStartDate,
                    enddate: formattedEndDate,
                    limit: 100,
                    timezoneoffset: 480,
                    ignoreschedulablepermission: true,
                    bypassscheduletimechecks: true // Correct parameter name
                });
                
                console.log('Alternate endpoint response:', altResponse);
                return altResponse.appointments || [];
            }
            
            return response.appointments || [];
        } catch (error) {
            console.error('getAvailableSlots error:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                config: error.config
            });
            throw new Error('Failed to get available slots: ' + error.message);
        }
    }

    async searchPatients(searchParams) {
        try {
            // Extract parameters from the searchParams object if it's an object
            let firstName = '', lastName = '', dob = null, email = '', phoneNumber = '';
            
            if (typeof searchParams === 'object' && searchParams !== null) {
                // Handle object parameter format (from updated UI)
                firstName = searchParams.firstName || '';
                lastName = searchParams.lastName || '';
                dob = searchParams.dob || null;
                email = searchParams.email || '';
                phoneNumber = searchParams.phoneNumber || '';
            } else {
                // This branch is kept for backward compatibility
                console.warn('Deprecated: Using string search parameter instead of object');
                // Try to parse as JSON if it's a string
                try {
                    const parsed = JSON.parse(searchParams);
                    firstName = parsed.firstName || '';
                    lastName = parsed.lastName || '';
                    dob = parsed.dob || null;
                    email = parsed.email || '';
                    phoneNumber = parsed.phoneNumber || '';
                } catch (e) {
                    // If parsing fails, assume it's a simple string query
                    firstName = searchParams || '';
                }
            }
            
            console.log('Searching for patient:', firstName, lastName, dob, email);
            
            // Build params for the API request
            const params = {};
            if (firstName) params.firstname = firstName;
            if (lastName) params.lastname = lastName;
            if (dob) params.dob = this.formatDate(new Date(dob));
            if (email) params.email = email;
            if (phoneNumber) params.mobilephone = phoneNumber;
            
            const response = await this.makeRequest('patients', 'GET', params);
            
            // Check if response has the expected structure with patients array
            if (response && response.patients && Array.isArray(response.patients)) {
                console.log(`Found ${response.patients.length} patients of ${response.totalcount} total`);
                return response.patients;
            }
            
            // If we get a different response structure, return it as is
            return response || [];
        } catch (error) {
            console.error('searchPatients error:', error);
            throw new Error('Failed to search patients: ' + (error.response?.data?.error || error.message));
        }
    }

    async searchPatientsEnhancedBestMatch(searchParams) {
        try {
            // Extract parameters from the searchParams object
            let firstName = '', lastName = '', dob = null, email = '', phoneNumber = '';
            
            if (typeof searchParams === 'object' && searchParams !== null) {
                // Handle object parameter format
                firstName = searchParams.firstName || '';
                lastName = searchParams.lastName || '';
                dob = searchParams.dob || null;
                email = searchParams.email || '';
                phoneNumber = searchParams.phoneNumber || '';
            } else {
                // Backward compatibility for string parameters
                console.warn('Deprecated: Using string search parameter instead of object');
                try {
                    const parsed = JSON.parse(searchParams);
                    firstName = parsed.firstName || '';
                    lastName = parsed.lastName || '';
                    dob = parsed.dob || null;
                    email = parsed.email || '';
                    phoneNumber = parsed.phoneNumber || '';
                } catch (e) {
                    firstName = searchParams || '';
                }
            }
            
            console.log('Enhanced best match search for patient:', { firstName, lastName, dob, email, phoneNumber });
            
            // Build params for the enhanced best match API request
            const params = {
                showinsurance: true
            };
            if (firstName) params.firstname = firstName;
            if (lastName) params.lastname = lastName;
            if (dob) params.dob = this.formatDate(new Date(dob));
            if (email) params.email = email;
            if (phoneNumber) params.mobilephone = phoneNumber;
            
            // Ensure we have at least some search criteria
            if (!firstName && !lastName && !dob && !email && !phoneNumber) {
                throw new Error('At least one search parameter is required (firstName, lastName, dob, email, or phoneNumber)');
            }
            
            const response = await this.makeRequest('patients/enhancedbestmatch', 'GET', params);
            
            // Enhanced best match returns an array directly or null
            if (response && Array.isArray(response)) {
                console.log(`Enhanced best match found ${response.length} patients`);
                return response;
            }
            
            // If no matches found, return empty array
            console.log('Enhanced best match found no patients');
            return [];
            
        } catch (error) {
            console.error('searchPatientsEnhancedBestMatch error:', error);
            throw new Error('Failed to search patients with enhanced best match: ' + (error.response?.data?.error || error.message));
        }
    }

     async getBookedAppointmentsreports(options = {}){
        console.log('===== getBookedAppointmentsReports: Starting =====');
        
        // Use provided dates or default to 12 months back to 60 days forward for comprehensive data
        let startdate, enddate;
        
        if (options.startdate) {
            startdate = options.startdate;
        } else {
            // Default: 12 months back for historical data
            const pastDate = new Date();
            pastDate.setMonth(pastDate.getMonth() - 12);
            startdate = this.formatDate(pastDate);
        }
        
        if (options.enddate) {
            enddate = options.enddate;
        } else {
            // Default: 60 days forward
            const futureDate = new Date();
            futureDate.setDate(futureDate.getDate() + 60);
            enddate = this.formatDate(futureDate);
        }
        
        console.log(`Date range: ${startdate} to ${enddate}`);
        
        try {
            console.log('Making API request to appointments/report...');
            
            const requestParams = {
                startdate,
                enddate,
                limit: options.limit || 1000,
                showpatientdetail: options.showpatientdetail !== false ? true : options.showpatientdetail,
                showinsurance: options.showinsurance !== false ? true : options.showinsurance,
                showpatientinstructions: options.showpatientinstructions !== false ? true : options.showpatientinstructions,
                ignorerestrictions: options.ignorerestrictions !== false ? true : options.ignorerestrictions
            };
            
            // Add departmentId if provided
            if (options.departmentId) {
                requestParams.departmentid = options.departmentId;
            }
            
            // Add providerId if provided
            if (options.providerId) {
                requestParams.providerid = options.providerId;
            }
            
            console.log('Request parameters:', requestParams);
            
            const response = await this.makeRequest(`appointments/report`, 'GET', requestParams);

            if (!response || Object.keys(response).length === 0) {
                console.warn("===== getBookedAppointmentsReports: No appointments found =====");
                return [];
            }

            console.log(`===== getBookedAppointmentsReports: Successfully retrieved ${Array.isArray(response) ? response.length : 'unknown number of'} appointments =====`);
            return response || [];
        } catch (error) {
            console.error("===== getBookedAppointmentsReports: ERROR =====", error);
            console.error("Error details:", {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });
            throw new Error('Failed to get appointment reports: ' + (error.response?.data?.error || error.message));
        }
    }

    async findOrCreateTestPatient({ firstname, lastname, dob, email, mobilephone, departmentid }) {
        try {
            const formatedDob = this.formatDate(new Date(dob));
            // Try to find existing patient
            const patients = await this.makeRequest('patients/enhancedbestmatch', 'GET', {
                firstname,
                lastname,
                dob :formatedDob
            });

            if (patients && patients.length > 0) {
                return patients[0];
            }

            // Create a new test patient
            const newPatient = await this.makeRequest('patients', 'POST', {
                departmentid,
                firstname,
                lastname,
                dob : formatedDob,
                email, 
                mobilephone
            });

            return newPatient;
        } catch (error) {
            throw new Error('Failed to find or create test patient: ' + (error.response?.data?.error || error.message));
        }
    }

    async bookAppointment({ appointmentid, patientid, appointmenttypeid, insuranceDetails, notes }) {
        try {
            // Validate appointment type ID
            if (!appointmenttypeid || isNaN(appointmenttypeid)) {
                console.error('❌ Invalid appointment type ID:', appointmenttypeid);
                throw new Error(`Invalid appointment type ID: ${appointmenttypeid}. Must be a valid number.`);
            }

            // Build request parameters
            const requestParams = {
                patientid: parseInt(patientid),
                appointmenttypeid: parseInt(appointmenttypeid),
                ignoreschedulablepermission: true
            };
            
            // Add notes if provided
            if (notes) {
                requestParams.bookingnote = notes;
                console.log('📝 Added booking note:', notes);
            }
            
            // Add insurance information if provided
            if (insuranceDetails) {
                console.log('🏥 Processing insurance details:', {
                    hasInsurance: insuranceDetails.insurance,
                    hasMember: insuranceDetails.insuranceMember,
                    hasGroup: insuranceDetails.insuranceGroup
                });
                // Create insurance info object with all required fields
                const insuranceInfo = {
                    insurancecompany: insuranceDetails.insurance || '',
                    insuranceidnumber: insuranceDetails.insuranceMember || '',
                    insurancegroupid: insuranceDetails.insuranceGroup || '',
                    insurancenote: insuranceDetails.notes || '',
                    insurancephone: insuranceDetails.insurancePhone || '',
                    insuranceplanname: insuranceDetails.insurancePlan || '',
                    insurancepolicyholder: insuranceDetails.policyHolder || '',
                    patientrelationshiptopolicyholder: insuranceDetails.relationship || ''
                };
                
                // Convert insurance info to JSON string as required by the API
                requestParams.insuranceinfo = JSON.stringify(insuranceInfo);
                console.log('🏥 Insurance info added to request');
            }
            
            console.log('🎯 BOOKING APPOINTMENT - Input Parameters:', {
                appointmentid,
                patientid,
                appointmenttypeid,
                hasInsurance: !!insuranceDetails,
                hasNotes: !!notes
            });

            console.log('📋 Appointment Type ID Details:', {
                original: appointmenttypeid,
                parsed: parseInt(appointmenttypeid),
                type: typeof appointmenttypeid,
                isValid: !isNaN(parseInt(appointmenttypeid))
            });
            
            console.log('🚀 Final booking request parameters:', requestParams);
            console.log('📞 Making API call to book appointment:', appointmentid);
            const response = await this.makeRequest(`appointments/${appointmentid}`, 'PUT', requestParams);
            
            console.log('✅ Appointment booking successful:', {
                appointmentid,
                appointmenttypeid: requestParams.appointmenttypeid,
                patientid: requestParams.patientid,
                responseKeys: Object.keys(response || {})
            });
            
            return response;
        } catch (error) {
            console.error('❌ APPOINTMENT BOOKING FAILED:', {
                appointmentid,
                appointmenttypeid,
                patientid,
                error: error.message,
                responseData: error.response?.data
            });
            throw new Error('Failed to book appointment: ' + (error.response?.data?.error || error.message));
        }
    }

    async getBookedAppointments(){
        console.log('===== getBookedAppointments: Starting =====');
        const today = new Date();
        const nextMonth = new Date(today);
        nextMonth.setDate(today.getDate() + 60);
    
        const startdate = this.formatDate(today);
        const enddate = this.formatDate(nextMonth);
        console.log(`Date range: ${startdate} to ${enddate}`);
        
        try {
            console.log('Making API request to appointments/report...');
            console.log('Request parameters:', {
                startdate: '03/01/2025',
                enddate,
                limit: 1000, // Increased from 100 to 200
                showpatientdetail: true,
                showinsurance:true,
                showpatientinstructions: true,
                ignorerestrictions: true
            });
            
            const response = await this.makeRequest(`appointments/report`, 'GET', {
                startdate: '03/01/2025',
                enddate,
                limit: 1000, // Increased from 100 to 200
                showpatientdetail: true,
                showinsurance:true,
                showpatientinstructions: true,
                ignorerestrictions: true
            });

            if (!response || Object.keys(response).length === 0) {
                console.warn("===== getBookedAppointments: No booked appointments found =====");
                return []; // Return empty array instead of throwing an error
            }

            console.log(`===== getBookedAppointments: Successfully retrieved ${Array.isArray(response) ? response.length : 'unknown number of'} appointments =====`);
            return response || [];
        } catch (error) {
            console.error("===== getBookedAppointments: ERROR =====", error);
            console.error("Error details:", {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });
            throw new Error('Failed to get booked appointments: ' + (error.response?.data?.error || error.message));
        }
    }

    async getAppointmentPatients (ids) {
        // Check if ids is an array or a Set
        const appointmentIds = Array.isArray(ids) ? ids : (ids instanceof Set ? Array.from(ids) : []);

        if (!appointmentIds || appointmentIds.length === 0) {
            console.warn('No appointment ids provided to getAppointmentPatients');
            // Return empty data structure instead of throwing an error
            return { appointments: [] };
        }
        console.log('Getting appointments for IDs:', appointmentIds);

        try {
            const response = await this.makeRequest('appointments/booked/multiple', 'GET', {
                appointmentids: appointmentIds.join(','),
                showpatientdetail: true,
                showclaimdetail: false,
                showinsurance: true
            });
            return response;
        }
        catch (error) { 
            console.error('Failed to get appointment patients:', error);
            // Return empty data structure instead of throwing
            return { appointments: [] };
        }
    }


    async getAppointmentReasonforNewPatient(departmentId, providerId) {
        try {
            console.log('Getting appointment reasons for department:', departmentId, 'and provider:', providerId);
            const response = await this.makeRequest('patientappointmentreasons/newpatient', 'GET', {
                departmentid: departmentId,
                providerid: providerId
            })
            if (!response || Object.keys(response).length === 0) {
                console.warn("No booked appointments found.");
                return [];
            }
            return response
        } catch (error) {
            throw new Error('Failed to get Appointment Reason for New Patient: ' + (error.response?.data?.error || error.message));
        }
    }

    async getAvailableSlots(departmentId, appointmentType, providerId, startDate, endDate) {
        console.log(appointmentType);
        try {
            console.log('Getting available slots:', {
                departmentId,
                providerId,
                startDate,
                endDate
            });

            // Ensure date format is consistent with what's used when creating slots (MM/DD/YYYY)
            // If startDate/endDate aren't already in this format, format them
            const formattedStartDate = typeof startDate === 'string' && startDate.includes('/') 
                ? startDate 
                : this.formatDate(new Date(startDate));
                
            const formattedEndDate = endDate ? (typeof endDate === 'string' && endDate.includes('/') 
                ? endDate 
                : this.formatDate(new Date(endDate)))
                : formattedStartDate; // If no end date, use start date

            // Add the required parameters for the API - making sure to match exactly what's used for creation
            const response = await this.makeRequest('appointments/open', 'GET', {
                departmentid: departmentId,
                providerid: providerId,
                appointmenttypeid: appointmentType.appointmenttypeid,
                appointmenttype: appointmentType.name,	
                startdate: formattedStartDate,
                enddate: formattedEndDate,
                bypassscheduletimechecks: true, // Correct parameter name per API docs
                ignoreschedulablepermission: true,
                showfullresponse: true,
                showallproviders: true,
                showallresources: true,
                limit: 200 // Add limit to ensure we get enough results
            });

            console.log('Full API Response:', response);
            if (!response.appointments || response.appointments.length === 0) {
                console.log('No slots found. Trying alternate endpoint...');
                
                // Try alternate endpoint format
                const altResponse = await this.makeRequest('appointments/open/customschedule', 'GET', {
                    departmentid: departmentId,
                    providerid: providerId,
                    startdate: formattedStartDate,
                    enddate: formattedEndDate,
                    limit: 100,
                    timezoneoffset: 480,
                    ignoreschedulablepermission: true,
                    bypassscheduletimechecks: true // Correct parameter name
                });
                
                console.log('Alternate endpoint response:', altResponse);
                return altResponse.appointments || [];
            }
            
            return response.appointments || [];
        } catch (error) {
            console.error('getAvailableSlots error:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                config: error.config
            });
            throw new Error('Failed to get available slots: ' + error.message);
        }
    }

    async getSpecificSlot(departmentId, providerId, appointmentTypeId, appointmentDate) {
        try {
            console.log('Fetching specific slot with params:', {
                departmentId,
                providerId,
                appointmentTypeId,
                appointmentDate
            });
            
            // Use the correct parameters as per API documentation
            const response = await this.makeRequest('appointments/open', 'GET', {
                departmentid: departmentId,
                providerid: providerId,
                appointmenttypeid: appointmentTypeId,
                // When getting slots, use startdate/enddate instead of appointmentdate
                startdate: appointmentDate, 
                enddate: appointmentDate,
                bypassscheduletimechecks: true, // Correct parameter name from API docs
                showfullresponse: true,
                showallproviders: true,
                showallresources: true,
                limit: 100 // Ensure we get enough results
            });
            
            console.log('Specific slot response:', response);
            return response;
        } catch (error) {
            console.error('Error fetching specific slot:', error);
            throw error;
        }
    }

    async createAvailabilitySlots(slotData) {
        try {
            if (!slotData.departmentid) {
                console.error('Missing required departmentid');
                throw new Error('Missing required departmentid');
            }

            if (!slotData.providerid) {
                console.error('Missing required providerid');
                throw new Error('Missing required providerid');
            }
            
            // Either appointmenttypeid or reasonid must be provided
            if (!slotData.appointmenttypeid && !slotData.reasonid) {
                console.error('Either appointmenttypeid or reasonid must be provided');
                throw new Error('Either appointmenttypeid or reasonid must be provided');
            }

            if (!slotData.appointmentdate) {
                console.error('Missing required appointmentdate');
                throw new Error('Missing required appointmentdate');
            }

            if (!slotData.appointmenttime || slotData.appointmenttime.length === 0) {
                console.error('Missing required appointmenttime');
                throw new Error('Missing required appointmenttime');
            }

            console.log('Creating availability slot:', slotData);
            
            // Validate required fields
            const endpoint = `/appointments/open`;
            const response = await this.makeRequest(endpoint, 'POST', slotData);
            
            console.log('Availability slot created:', response);
            return response;
        } catch (error) {
            console.error('Error creating availability slot:', error);
            throw error;
        }
    }

    async getPatientsFromAppointments(options = {}) {
        try {
            console.log('Getting patients from appointments...');
            // First, get all booked appointments
            const appointmentsData = await this.getBookedAppointments(options);
            console.log('Appointments data for patient extraction:', appointmentsData);
            
            // Extract patient data from appointments 
            let patients = [];
            let patientIds = new Set();
            
            // Handle different appointment data formats
            let appointments = [];
            if (Array.isArray(appointmentsData)) {
                appointments = appointmentsData;
            } else if (appointmentsData && typeof appointmentsData === 'object') {
                appointments = [
                    ...(appointmentsData.scheduled || []),
                    ...(appointmentsData.cancelled || []),
                    ...(appointmentsData.rescheduled || [])
                ];
            }
            
            // Extract unique patients from appointments
            appointments.forEach(apt => {
                if (apt.patient && apt.patient.patientid && !patientIds.has(apt.patient.patientid)) {
                    patientIds.add(apt.patient.patientid);
                    patients.push(apt.patient);
                }
            });
            
            console.log(`Found ${patients.length} unique patients from appointments`);
            return patients;
        } catch (error) {
            console.error('Failed to get patients from appointments:', error);
            // If API call fails, try to get patients from localStorage
            try {
                const localPatients = JSON.parse(localStorage.getItem('patients') || '[]');
                return localPatients;
            } catch (localError) {
                console.error('Failed to get patients from localStorage:', localError);
                return [];
            }
        }
    }

    formatDate(date) {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    }

    // COMPREHENSIVE ENDPOINTS BASED ON TEST METHODS

    // Practice Information
    async getPracticeInfo() {
        try {
            console.log('Getting practice information...');
            const response = await this.makeRequest('practiceinfo', 'GET');
            return response;
        } catch (error) {
            console.error('getPracticeInfo error:', error);
            throw new Error('Failed to get practice info: ' + (error.response?.data?.error || error.message));
        }
    }

    // Departments
    async getDepartments(options = {}) {
        try {
            console.log('Getting departments...');
            const params = {
                showalldepartments: options.showAll || true,
                limit: options.limit || 200,
                providerlist: options.includeProviders || true
            };
            const response = await this.makeRequest('departments', 'GET', params);
            console.log(`Found ${response.departments?.length || 0} departments`);
            return response.departments || [];
        } catch (error) {
            console.error('getDepartments error:', error);
            throw new Error('Failed to get departments: ' + (error.response?.data?.error || error.message));
        }
    }

    // Providers
    async getProviders(departmentId, options = {}) {
        try {
            console.log('Getting providers for department:', departmentId);
            const params = {
                departmentid: departmentId,
                showallproviders: options.showAll || true,
                limit: options.limit || 100
            };
            const response = await this.makeRequest('providers', 'GET', params);
            console.log(`Found ${response.providers?.length || 0} providers`);
            return response.providers || [];
        } catch (error) {
            console.error('getProviders error:', error);
            throw new Error('Failed to get providers: ' + (error.response?.data?.error || error.message));
        }
    }

    async getAllProviders() {
        try {
            console.log('Getting all providers...');
            // Simple call with departmentId 1 and directly setting showallproviders: true in the parameters
            const response = await this.makeRequest('providers', 'GET', {
                departmentid: '1',
                showallproviders: true,
                limit: 100
            });
            
            const allProviders = response.providers || [];
            console.log(`Found ${allProviders.length} providers`);
            return allProviders;
        } catch (error) {
            console.error('getAllProviders error:', error);
            return []; // Return empty array on error to prevent application crashes
        }
    }

    // Appointment Types
    async getAppointmentTypes(departmentId, providerId, options = {}) {
        try {
            console.log('Getting appointment types for department:', departmentId, 'and provider:', providerId);
            const params = {
                departmentid: departmentId,
                providerid: providerId,
                showdeleted: options.showDeleted || false,
                showfulldetails: options.showFullDetails || true,
                showalltypes: options.showAllTypes || true,
                limit: options.limit || 100
            };
            
            // Using the appropriate endpoint based on test methods
            const endpoint = options.useConfigEndpoint ? 'configuration/appointmenttypes' : 'appointmenttypes';
            
            const response = await this.makeRequest(endpoint, 'GET', params);
            console.log(`Found ${Array.isArray(response) ? response.length : (response.appointmenttypes?.length || 0)} appointment types`);
            
            return options.useConfigEndpoint ? response : (response.appointmenttypes || []);
        } catch (error) {
            console.error('getAppointmentTypes error:', error);
            throw new Error('Failed to get appointment types: ' + (error.response?.data?.error || error.message));
        }
    }

    // // Patients
    // async searchPatients(firstName, lastName, dob) {
    //     try {
    //         console.log('Searching for patient:', firstName, lastName, dob);
    //         const params = {
    //             firstname: firstName
    //         };
    //         const response = await this.makeRequest('patients', 'GET', params);
            
    //         // Check if response has the expected structure with patients array
    //         if (response && response.patients && Array.isArray(response.patients)) {
    //             console.log(`Found ${response.patients.length} patients of ${response.totalcount} total`);
    //             return response.patients;
    //         }
            
    //         // If we get a different response structure, return it as is
    //         return response || [];
    //     } catch (error) {
    //         console.error('searchPatients error:', error);
    //         throw new Error('Failed to search patients: ' + (error.response?.data?.error || error.message));
    //     }
    // }

    async createPatient(patientData) {
        try {
            console.log('Creating new patient:', patientData.firstname || patientData.firstName, patientData.lastname || patientData.lastName);
            
            // Format date of birth to MM/DD/YYYY
            let dob = patientData.dob || patientData.dateOfBirth;
            if (dob && !dob.includes('/')) {
                // If date is in YYYY-MM-DD format, convert it to MM/DD/YYYY
                const dobDate = new Date(dob);
                if (!isNaN(dobDate.getTime())) {
                    dob = this.formatDate(dobDate);
                }
            }
            
            // Format data to match Athena API requirements
            const formattedData = {
                departmentid: patientData.departmentid || patientData.departmentId || 1,
                firstname: patientData.firstname || patientData.firstName,
                lastname: patientData.lastname || patientData.lastName,
                dob: dob,
                address1: patientData.address1 || patientData.address || '',
                city: patientData.city || '',
                state: patientData.state || '',
                zip: patientData.zip || patientData.zipCode || '',
                email: patientData.email || '',
                mobilephone: patientData.mobilephone || patientData.phone || '',
                countrycode3166: patientData.countrycode3166 || 'US',
                guarantoraddresssameaspatient: true,
                portalaccessgiven: true,
                privacyinformationverified: true
            };

            // Only include sex if provided and not empty; normalize to M/F
            if (patientData.sex && String(patientData.sex).trim() !== '') {
                const rawSex = String(patientData.sex).trim();
                const normalizedSex = rawSex.toUpperCase() === 'MALE' ? 'M'
                    : rawSex.toUpperCase() === 'FEMALE' ? 'F'
                    : (rawSex.toUpperCase() === 'M' || rawSex.toUpperCase() === 'F') ? rawSex.toUpperCase() : undefined;
                if (normalizedSex) {
                    formattedData.sex = normalizedSex;
                }
            }
            
            console.log('Formatted patient data for API:', formattedData);
            const response = await this.makeRequest('patients', 'POST', formattedData);
            console.log('Patient creation response:', response);
            return response;
        } catch (error) {
            console.error('createPatient error:', error);
            
            // Better error handling with proper error extraction
            let errorMessage = '';
            
            if (error.response?.data?.error?.error) {
                // Handle nested error object structure from Athena API
                errorMessage = error.response.data.error.error;
            } else if (error.response?.data?.error && typeof error.response.data.error === 'object') {
                // Object error needs to be stringified
                errorMessage = JSON.stringify(error.response.data.error);
            } else if (error.response?.data?.error) {
                // String error
                errorMessage = error.response.data.error;
            } else if (error.response?.data?.message) {
                // Some APIs use message instead of error
                errorMessage = error.response.data.message;
            } else if (error.message) {
                // Default to error message
                errorMessage = error.message;
            } else {
                // Last resort
                errorMessage = 'Unknown error occurred';
            }
            
            throw new Error('Failed to create patient: ' + errorMessage);
        }
    }

    async updatePatient(patientId, patientData) {
        try {
            console.log(`Updating patient with ID ${patientId}:`, patientData);
            
            // Format date of birth to MM/DD/YYYY if provided
            let dob = patientData.dob || patientData.dateOfBirth;
            if (dob && !dob.includes('/')) {
                // If date is in YYYY-MM-DD format, convert it to MM/DD/YYYY
                const dobDate = new Date(dob);
                if (!isNaN(dobDate.getTime())) {
                    dob = this.formatDate(dobDate);
                }
            }
            
            // Format data to match Athena API requirements
            const formattedData = {
                departmentid: patientData.departmentid || patientData.departmentId || 1,
                firstname: patientData.firstname || patientData.firstName,
                lastname: patientData.lastname || patientData.lastName,
            };
            
            // Only include fields that are actually being updated
            if (dob) formattedData.dob = dob;
            if (patientData.address1 || patientData.address) formattedData.address1 = patientData.address1 || patientData.address;
            if (patientData.city) formattedData.city = patientData.city;
            if (patientData.state) formattedData.state = patientData.state;
            if (patientData.zip || patientData.zipCode) formattedData.zip = patientData.zip || patientData.zipCode;
            if (patientData.email) formattedData.email = patientData.email;
            if (patientData.mobilephone || patientData.phone) formattedData.mobilephone = patientData.mobilephone || patientData.phone;
            if (patientData.homephone) formattedData.homephone = patientData.homephone;
            if (patientData.countrycode3166) formattedData.countrycode3166 = patientData.countrycode3166 || 'US';
            if (patientData.sex) formattedData.sex = patientData.sex;
            if (patientData.maritalstatus) formattedData.maritalstatus = patientData.maritalstatus;
            if (patientData.race) formattedData.race = patientData.race;
            if (patientData.ethnicitycode) formattedData.ethnicitycode = patientData.ethnicitycode;
            if (patientData.language6392code) formattedData.language6392code = patientData.language6392code;
            if (patientData.notes) formattedData.notes = patientData.notes;
            
            console.log('Formatted patient data for update API:', formattedData);
            const endpoint = `patients/${patientId}`;
            const response = await this.makeRequest(endpoint, 'PUT', formattedData);
            console.log('Patient update response:', response);
            
            // Get updated patient data
            const updatedPatient = await this.getPatientById(patientId);
            return updatedPatient;
        } catch (error) {
            console.error('updatePatient error:', error);
            throw new Error('Failed to update patient: ' + (error.response?.data?.error || error.message));
        }
    }
    
    async getPatientById(patientId, options = {}) {
        try {
            console.log(`Getting patient with ID: ${patientId}`);
            const params = {};
            
            // Add showinsurance to params if true
            if (options.showinsurance) {
                params.showinsurance = 'true';
            }
            
            const response = await this.makeRequest(`patients/${patientId}`, 'GET', params);
            return response;
        } catch (error) {
            console.error('getPatientById error:', error);
            throw new Error('Failed to get patient: ' + (error.response?.data?.error || error.message));
        }
    }

    async findOrCreatePatient(patientData) {
        try {
            // Format date if needed
            const formattedDob = patientData.dob.includes('/') ? patientData.dob : this.formatDate(new Date(patientData.dob));
            
            // First try to find the patient
            const patients = await this.searchPatients(
                patientData.firstname || patientData.firstName, 
                patientData.lastname || patientData.lastName, 
                formattedDob
            );

            if (patients && patients.length > 0) {
                console.log('Found existing patient:', patients[0]);
                return patients[0];
            }

            // If not found, create a new patient
            console.log('No existing patient found, creating new patient');
            
            // Format the data for Athena API
            const formattedData = {
                departmentid: patientData.departmentid || patientData.departmentId || 1,
                firstname: patientData.firstname || patientData.firstName,
                lastname: patientData.lastname || patientData.lastName,
                dob: formattedDob,
                email: patientData.email,
                mobilephone: patientData.mobilephone || patientData.phone,
                address1: patientData.address1 || patientData.address,
                city: patientData.city,
                state: patientData.state,
                zip: patientData.zip || patientData.zipCode,
                countrycode3166: patientData.countrycode3166 || 'US',
                guarantoraddresssameaspatient: true,
                portalaccessgiven: true,
                privacyinformationverified: true
            };

            const newPatient = await this.createPatient(formattedData);
            console.log('Created new patient:', newPatient);
            return newPatient;
        } catch (error) {
            console.error('findOrCreatePatient error:', error);
            throw new Error('Failed to find or create patient: ' + (error.response?.data?.error || error.message));
        }
    }

    async archivePatient(patientId) {
        try {
            console.log(`Archiving patient with ID: ${patientId}`);
            
            // Format data to set patient as inactive
            const formattedData = {
                status: 'inactive'
            };
            
            // Use PUT method to update the patient status
            const endpoint = `patients/${patientId}`;
            const response = await this.makeRequest(endpoint, 'PUT', formattedData);
            console.log('Patient archive response:', response);
            return response;
        } catch (error) {
            console.error('archivePatient error:', error);
            throw new Error('Failed to archive patient: ' + (error.response?.data?.error || error.message));
        }
    }

    async restorePatient(patientId) {
        try {
            console.log(`Restoring patient with ID: ${patientId}`);
            
            // Format data to set patient as active
            const formattedData = {
                status: 'active'
            };
            
            // Use PUT method to update the patient status
            const endpoint = `patients/${patientId}`;
            const response = await this.makeRequest(endpoint, 'PUT', formattedData);
            console.log('Patient restore response:', response);
            return response;
        } catch (error) {
            console.error('restorePatient error:', error);
            throw new Error('Failed to restore patient: ' + (error.response?.data?.error || error.message));
        }
    }

    // Appointment Scheduling
    async getOpenAppointmentSlots(departmentId, appointmentTypeId, startDate, providerId, endDate) {
        try {
            console.log('Getting open appointment slots with params:', {
                departmentId,
                appointmentTypeId,
                startDate,
                providerId
            });
            
            // Ensure department ID is present (required parameter)
            if (!departmentId) {
                throw new Error('Missing required parameter: departmentid');
            }
            
            // Use endDate if provided, otherwise use startDate
            const apiEndDate = endDate || startDate;
            
            // Build params object, only including appointmenttypeid if it's provided
            const params = {
                departmentid: departmentId,
                startdate: startDate,
                enddate: apiEndDate,
                showfullresponse: true,
                ignoreschedulablepermission: true,
                showallproviders: Boolean(providerId) ? false : true,
                showallresources: true
            };
            
            // Only add appointmenttypeid if it's provided and not null
            if (appointmentTypeId) {
                params.appointmenttypeid = appointmentTypeId;
            }
            
            // Only add providerid if it's provided and not null
            if (providerId) {
                params.providerid = providerId;
            }
            
            console.log('Final API params:', params);
            
            if (apiEndDate && apiEndDate !== startDate) {
                params.enddate = apiEndDate;
            }
            
            const response = await this.makeRequest('appointments/open', 'GET', params);
            
            if (response && response.appointments) {
                console.log(`Found ${response.appointments.length} open slots before deduplication`);
                
                // Apply deduplication filter
                const uniqueAppointments = this.filterDuplicateSlots(response.appointments);
                
                console.log(`Returning ${uniqueAppointments.length} unique open slots after deduplication`);
                return uniqueAppointments;
            }
            
            return [];
        } catch (error) {
            console.error('Failed to get open appointment slots:', error);
            throw new Error('Failed to get open appointment slots: ' + (error.response?.data?.error || error.message));
        }
    }

        // Appointment Scheduling
    async getOpenAppointmentSlots(departmentId, appointmentTypeId, startDate, providerId, endDate) {
        try {
            console.log('Getting open appointment slots with params:', {
                departmentId,
                appointmentTypeId,
                startDate,
                providerId
            });
            
            // Ensure department ID is present (required parameter)
            if (!departmentId) {
                throw new Error('Missing required parameter: departmentid');
            }
            
            // Use endDate if provided, otherwise use startDate
            const apiEndDate = endDate || startDate;
            
            // Build params object, only including appointmenttypeid if it's provided
            const params = {
                departmentid: departmentId,
                startdate: startDate,
                enddate: apiEndDate,
                showfullresponse: true,
                ignoreschedulablepermission: false,
                showallproviders: Boolean(providerId) ? false : true,
                showallresources: true
            };
            
            // Only add appointmenttypeid if it's provided and not null
            if (appointmentTypeId) {
                params.appointmenttypeid = appointmentTypeId;
            }
            
            // Only add providerid if it's provided and not null
            if (providerId) {
                params.providerid = providerId;
            }
            
            console.log('Final API params:', params);
            
            if (apiEndDate && apiEndDate !== startDate) {
                params.enddate = apiEndDate;
            }
            
            const response = await this.makeRequest('appointments/open', 'GET', params);
            
            if (response && response.appointments) {
                console.log(`Found ${response.appointments.length} open slots before deduplication`);
                
                // Apply deduplication filter
                const uniqueAppointments = this.filterDuplicateSlots(response.appointments);
                
                console.log(`Returning ${uniqueAppointments.length} unique open slots after deduplication`);
                return uniqueAppointments;
            }
            
            return [];
        } catch (error) {
            console.error('Failed to get open appointment slots:', error);
            throw new Error('Failed to get open appointment slots: ' + (error.response?.data?.error || error.message));
        }
    }
    // Appointment Scheduling
    async getOpenAppointmentSlotsByReason(departmentId, reasonId, startDate, providerId, endDate) {
        try {
            console.log('Getting open appointment slots with params:', {
                departmentId,
                reasonId,
                startDate,
                providerId
            });
            
            // Ensure department ID is present (required parameter)
            if (!departmentId) {
                throw new Error('Missing required parameter: departmentid');
            }
            
            // Use endDate if provided, otherwise use startDate
            const apiEndDate = endDate || startDate;
            
            // Build params object, only including appointmenttypeid if it's provided
            const params = {
                departmentid: departmentId,
                startdate: startDate,
                enddate: apiEndDate,
                showfullresponse: true,
                ignoreschedulablepermission: false,
                showallproviders: Boolean(providerId) ? false : true,
                showallresources: true
            };
            
            // Only add reasonId if it's provided and not null
            if (reasonId) {
                params.reasonid = reasonId;
            }
            
            // Only add providerid if it's provided and not null
            if (providerId) {
                params.providerid = providerId;
            }
            
            console.log('Final API params:', params);
            
            if (apiEndDate && apiEndDate !== startDate) {
                params.enddate = apiEndDate;
            }
            
            const response = await this.makeRequest('appointments/open', 'GET', params);
            
            if (response && response.appointments) {
                console.log(`Found ${response.appointments.length} open slots before deduplication`);
                
                // Apply deduplication filter
                const uniqueAppointments = this.filterDuplicateSlots(response.appointments);
                
                console.log(`Returning ${uniqueAppointments.length} unique open slots after deduplication`);
                return uniqueAppointments;
            }
            
            return [];
        } catch (error) {
            console.error('Failed to get open appointment slots:', error);
            throw new Error('Failed to get open appointment slots: ' + (error.response?.data?.error || error.message));
        }
    }

    async getCustomScheduleSlots(params) {
        try {
            console.log('Getting custom schedule slots with params:', params);
            const response = await this.makeRequest('appointments/open/customschedule', 'GET', params);
            console.log(`Found ${response.appointments?.length || 0} custom schedule slots`);
            return response.appointments || [];
        } catch (error) {
            console.error('getCustomScheduleSlots error:', error);
            throw new Error('Failed to get custom schedule slots: ' + error.message);
        }
    }

    async bookAppointment({ appointmentid, patientid, appointmenttypeid, insuranceDetails, notes }) {
        try {
            // Validate appointment type ID
            if (!appointmenttypeid || isNaN(appointmenttypeid)) {
                console.error('❌ Invalid appointment type ID:', appointmenttypeid);
                throw new Error(`Invalid appointment type ID: ${appointmenttypeid}. Must be a valid number.`);
            }

            // Build request parameters
            const requestParams = {
                patientid: parseInt(patientid),
                appointmenttypeid: parseInt(appointmenttypeid),
                ignoreschedulablepermission: true
            };
            
            // Add notes if provided
            if (notes) {
                requestParams.bookingnote = notes;
                console.log('📝 Added booking note:', notes);
            }
            
            // Add insurance information if provided
            if (insuranceDetails) {
                console.log('🏥 Processing insurance details:', {
                    hasInsurance: insuranceDetails.insurance,
                    hasMember: insuranceDetails.insuranceMember,
                    hasGroup: insuranceDetails.insuranceGroup
                });
                // Create insurance info object with all required fields
                const insuranceInfo = {
                    insurancecompany: insuranceDetails.insurance || '',
                    insuranceidnumber: insuranceDetails.insuranceMember || '',
                    insurancegroupid: insuranceDetails.insuranceGroup || '',
                    insurancenote: insuranceDetails.notes || '',
                    insurancephone: insuranceDetails.insurancePhone || '',
                    insuranceplanname: insuranceDetails.insurancePlan || '',
                    insurancepolicyholder: insuranceDetails.policyHolder || '',
                    patientrelationshiptopolicyholder: insuranceDetails.relationship || ''
                };
                
                // Convert insurance info to JSON string as required by the API
                requestParams.insuranceinfo = JSON.stringify(insuranceInfo);
                console.log('🏥 Insurance info added to request');
            }
            
            console.log('🎯 BOOKING APPOINTMENT - Input Parameters:', {
                appointmentid,
                patientid,
                appointmenttypeid,
                hasInsurance: !!insuranceDetails,
                hasNotes: !!notes
            });

            console.log('📋 Appointment Type ID Details:', {
                original: appointmenttypeid,
                parsed: parseInt(appointmenttypeid),
                type: typeof appointmenttypeid,
                isValid: !isNaN(parseInt(appointmenttypeid))
            });
            
            console.log('🚀 Final booking request parameters:', requestParams);
            console.log('📞 Making API call to book appointment:', appointmentid);
            const response = await this.makeRequest(`appointments/${appointmentid}`, 'PUT', requestParams);
            
            console.log('✅ Appointment booking successful:', {
                appointmentid,
                appointmenttypeid: requestParams.appointmenttypeid,
                patientid: requestParams.patientid,
                responseKeys: Object.keys(response || {})
            });
            
            return response;
        } catch (error) {
            console.error('❌ APPOINTMENT BOOKING FAILED:', {
                appointmentid,
                appointmenttypeid,
                patientid,
                error: error.message,
                responseData: error.response?.data
            });
            throw new Error('Failed to book appointment: ' + (error.response?.data?.error || error.message));
        }
    }

    // Appointment Reporting
    async getAppointmentReport(startDate, endDate, options = {}) {
        try {
            console.log('Getting appointment report from', startDate, 'to', endDate);
            
            const formattedStartDate = startDate.includes('/') ? startDate : this.formatDate(new Date(startDate));
            const formattedEndDate = endDate.includes('/') ? endDate : this.formatDate(new Date(endDate));
            
            const params = {
                startdate: formattedStartDate,
                enddate: formattedEndDate,
                limit: options.limit || 100,
                showpatientdetail: options.showPatientDetail || true,
                showpatientinstructions: options.showPatientInstructions || true,
                ignorerestrictions: options.ignoreRestrictions || true
            };
            
            const response = await this.makeRequest('appointments/report', 'GET', params);
            
            console.log(`Retrieved ${Array.isArray(response) ? response.length : '0'} appointments from report`);
            return response || [];
        } catch (error) {
            console.error('getAppointmentReport error:', error);
            throw new Error('Failed to get appointment report: ' + (error.response?.data?.error || error.message));
        }
    }

    async getBookedAppointmentsByIds(appointmentIds) {
        try {
            if (!appointmentIds || appointmentIds.length === 0) {
                console.warn('No appointment IDs provided');
                return { appointments: [] };
            }
            
            console.log('Getting booked appointments by IDs:', appointmentIds);
            const response = await this.makeRequest('appointments/booked/multiple', 'GET', {
                appointmentids: Array.isArray(appointmentIds) ? appointmentIds.join(',') : appointmentIds,
                showpatientdetail: true,
                showclaimdetail: false,
                showinsurance: true
            });
            
            console.log(`Retrieved ${response.appointments?.length || 0} booked appointments by IDs`);
            return response || { appointments: [] };
        } catch (error) {
            console.error('getBookedAppointmentsByIds error:', error);
            throw new Error('Failed to get booked appointments by IDs: ' + (error.response?.data?.error || error.message));
        }
    }

    // Appointment Reasons
    async getAppointmentCancelReasons() {
        try {
            const endpoint = 'appointmentcancelreasons';
            const response = await this.makeRequest(endpoint,'GET');
            
            // Extract the appointment cancel reasons from the response
            if (response && response.appointmentcancelreasons) {
                return response.appointmentcancelreasons.map(reason => ({
                    id: reason.appointmentcancelreasonid,
                    description: reason.name,
                    isNoShow: reason.noshow,
                    patientRescheduled: reason.patientrescheduled,
                    slotAvailable: reason.slotavailable,
                    providerUnavailable: reason.providerunavailable
                }));
            }
            
            return [];
        } catch (error) {
            console.error('Error getting appointment cancel reasons:', error);
            throw error;
        }
    }

    async getAppointmentReasons(departmentId, providerId) {
        try {
            console.log('Getting appointment reasons for department:', departmentId, 'and provider:', providerId);
            const response = await this.makeRequest('patientappointmentreasons', 'GET', {
                departmentid: departmentId,
                providerid: providerId
            });
            
            // Ensure we return an array
            if (Array.isArray(response)) {
                return response;
            } else if (response && typeof response === 'object') {
                // If it's an object with a reasons property
                if (Array.isArray(response.reasons)) {
                    return response.reasons;
                }
                // If it's an object with patientappointmentreasons property
                if (Array.isArray(response.patientappointmentreasons)) {
                    return response.patientappointmentreasons;
                }
                // If it's just an object with id/description pairs, convert to array
                if (Object.keys(response).length > 0) {
                    return Object.keys(response).map(key => ({
                        reasonid: key,
                        description: response[key]
                    }));
                }
            }
            
            // Default to empty array
            console.log('No appointment reasons found or unexpected format, using empty array');
            return [];
        } catch (error) {
            console.error('getAppointmentReasons error:', error);
            // Return default reasons instead of throwing
            return [
                { reasonid: 1, description: "Regular Checkup" },
                { reasonid: 2, description: "Follow-up Visit" },
                { reasonid: 3, description: "Consultation" },
                { reasonid: 4, description: "Urgent Care" }
            ];
        }
    }

    async getNewPatientAppointmentReasons(departmentId, providerId) {
        try {
            console.log('Getting new patient appointment reasons for department:', departmentId, 'and provider:', providerId);
            const response = await this.makeRequest('patientappointmentreasons/newpatient', 'GET', {
                departmentid: departmentId,
                providerid: providerId
            });
            
            // Ensure we return an array
            if (Array.isArray(response)) {
                return response;
            } else if (response && typeof response === 'object') {
                // If it's an object with a reasons property
                if (Array.isArray(response.reasons)) {
                    return response.reasons;
                }
                // If it's an object with patientappointmentreasons property
                if (Array.isArray(response.patientappointmentreasons)) {
                    return response.patientappointmentreasons;
                }
                // If it's just an object with id/description pairs, convert to array
                if (Object.keys(response).length > 0) {
                    return Object.keys(response).map(key => ({
                        reasonid: key,
                        description: response[key]
                    }));
                }
            }
            
            // Default to empty array
            console.log('No new patient appointment reasons found or unexpected format, using empty array');
            return [];
        } catch (error) {
            console.error('getNewPatientAppointmentReasons error:', error);
            // Return default reasons instead of throwing
            return [
                { reasonid: 1, description: "New Patient Consultation" },
                { reasonid: 2, description: "Initial Assessment" },
                { reasonid: 3, description: "New Patient Physical" },
                { reasonid: 4, description: "First Time Visit" }
            ];
        }
    }

    // Helper to filter duplicate slots by date and time
    filterDuplicateSlots(slots) {
        // Create a map to deduplicate by date and starttime
        const uniqueSlots = new Map();
        
        slots.forEach(slot => {
            const key = `${slot.date}_${slot.starttime}`;
            // Only keep the first occurrence of each date+time combination
            if (!uniqueSlots.has(key)) {
                uniqueSlots.set(key, slot);
            }
        });
        
        // Convert the map values back to an array
        return Array.from(uniqueSlots.values());
    }

    async getBookedAppointments(options = {}) {
        try {
            console.log('Getting booked appointments with options:', JSON.stringify(options));
            
            // Format dates if provided
            let startDate = options.startDate;
            let endDate = options.endDate;
            
            if (startDate && !startDate.includes('/')) {
                startDate = this.formatDate(new Date(startDate));
            }
            
            if (endDate && !endDate.includes('/')) {
                endDate = this.formatDate(new Date(endDate));
            }
            
            const today = new Date();
            const nextMonth = new Date(today);
            nextMonth.setDate(today.getDate() + 30);
            
            // Build parameters for the API request
            const params = {
                startdate: startDate || this.formatDate(today),
                enddate: endDate || this.formatDate(nextMonth),
                limit: options.limit || 1000,
                offset: options.offset || 0,
                showpatientdetail: true,
                showinsurance:true,
                showcancelled: true
            };
            
            // Add optional filters if provided
            if (options.departmentId) params.departmentid = options.departmentId;
            if (options.providerId) {
                // For providerid, use the first value if it's an array
                // or just use the single value
                params.providerid = Array.isArray(options.providerId) 
                    ? options.providerId[0]  // Just use the first provider ID 
                    : options.providerId;
            }
            if (options.patientId) params.patientid = options.patientId;
            if (options.appointmentTypeId) params.appointmenttypeid = options.appointmentTypeId;
            
            // Additional parameters that might be useful
            if (options.showInsurance) params.showinsurance = true;
            if (options.showClaimDetail) params.showclaimdetail = true;
            if (options.showPatientInstructions) params.showpatientinstructions = true;
            
            console.log('Request parameters:', params);
            
            const response = await this.makeRequest('appointments/booked', 'GET', params);
            
            if (!response || !response.appointments) {
                console.warn("No booked appointments found");
                return [];
            }
            
            console.log(`Retrieved ${response.appointments.length} booked appointments of ${response.totalcount} total`);
            return response.appointments || [];
        } catch (error) {
            console.error('getBookedAppointments error:', error);
            console.error('Error details:', {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });
            
            // Fallback to local storage if available
            try {
                const localAppointments = JSON.parse(localStorage.getItem('appointments') || '[]');
                if (localAppointments.length > 0) {
                    console.log('Falling back to localStorage appointments');
                    return localAppointments;
                }
            } catch (localError) {
                console.error('LocalStorage fallback failed:', localError);
            }
            
            throw new Error('Failed to get booked appointments: ' + (error.response?.data?.error || error.message));
        }
    }

    async getBookedAppointmentsByIds(appointmentIds) {
        try {
            if (!appointmentIds || appointmentIds.length === 0) {
                console.warn('No appointment IDs provided');
                return { appointments: [] };
            }
            
            console.log('Getting booked appointments by IDs:', appointmentIds);
            const response = await this.makeRequest('appointments/booked/multiple', 'GET', {
                appointmentids: Array.isArray(appointmentIds) ? appointmentIds.join(',') : appointmentIds,
                showpatientdetail: true,
                showclaimdetail: false,
                showinsurance: false
            });
            
            console.log(`Retrieved ${response.appointments?.length || 0} booked appointments by IDs`);
            return response || { appointments: [] };
        } catch (error) {
            console.error('getBookedAppointmentsByIds error:', error);
            throw new Error('Failed to get booked appointments by IDs: ' + (error.response?.data?.error || error.message));
        }
    }

    /**
     * Get appointment cancel reasons from Athena Health API
     * 
     * @returns {Promise<Array>} - Array of cancel reasons
     */
    async getAppointmentCancelReasons() {
        try {
            const response = await this.makeRequest('appointmentcancelreasons', 'GET');
            return response;
        } catch (error) {
            console.error('Failed to get appointment cancel reasons:', error);
            throw new Error('Failed to get appointment cancel reasons: ' + (error.response?.data?.error || error.message));
        }
    }

    /**
     * Cancels an existing appointment
     * 
     * @param {Object} params - Parameters for cancelling an appointment
     * @param {number} params.appointmentId - The ID of the appointment to cancel
     * @param {number} [params.patientId] - The patient ID for the appointment
     * @param {number} [params.appointmentCancelReasonId] - Optional cancel reason ID (from getAppointmentCancelReasons)
     * @param {string} [params.cancellationReason] - Text explanation for the cancellation
     * @param {boolean} [params.ignoreSchedulablePermission=true] - Whether to bypass web scheduling permissions
     * @param {boolean} [params.noPatientCase=false] - Whether to bypass creating a patient case
     * @returns {Promise<Object>} - API response with status of the operation
     */
    async cancelAppointment(params) {
        if (!params.appointmentId) {
            throw new Error('Appointment ID is required to cancel an appointment');
        }

        try {
            const endpoint = `appointments/${params.appointmentId}/cancel`;
            const requestParams = {
                patientid: params.patientId,
                appointmentcancelreasonid: params.appointmentCancelReasonId,
                cancellationreason: params.cancellationReason || 'Appointment cancelled',
                ignoreschedulablepermission: params.ignoreSchedulablePermission !== false, // Default to true
                nopatientcase: params.noPatientCase || false
            };

            console.log('Cancelling appointment:', params.appointmentId, 'with params:', requestParams);
            const response = await this.makeRequest(endpoint, 'PUT', requestParams);
            return response;
        } catch (error) {
            console.error('Failed to cancel appointment:', error);
            throw new Error('Failed to cancel appointment: ' + (error.response?.data?.error || error.message));
        }
    }

    /**
     * Reschedules an existing appointment to a new timeslot
     * 
     * @param {Object} params - Parameters for rescheduling an appointment
     * @param {number} params.appointmentId - The ID of the current appointment
     * @param {number} params.newAppointmentId - The ID of the new appointment slot
     * @param {number} params.patientId - The patient ID for the appointment
     * @param {number} [params.appointmentCancelReasonId] - Cancel reason ID for the original appointment
     * @param {string} [params.rescheduleReason] - Text explanation for rescheduling
     * @param {number} [params.reasonId] - The appointment reason ID (defaults to original reason if not provided)
     * @param {boolean} [params.ignoreSchedulablePermission=true] - Whether to bypass web scheduling permissions
     * @param {boolean} [params.noPatientCase=false] - Whether to bypass creating a patient case
     * @returns {Promise<Object>} - API response with status of the operation
     */
    async rescheduleAppointment(params) {
        if (!params.appointmentId) {
            throw new Error('Current appointment ID is required to reschedule');
        }
        if (!params.newAppointmentId) {
            throw new Error('New appointment ID is required to reschedule');
        }
        if (!params.patientId) {
            throw new Error('Patient ID is required to reschedule an appointment');
        }

        try {
            const endpoint = `appointments/${params.appointmentId}/reschedule`;
            const requestParams = {
                newappointmentid: params.newAppointmentId,
                patientid: params.patientId,
                appointmentcancelreasonid: params.appointmentCancelReasonId,
                reschedulereason: params.rescheduleReason || 'Appointment rescheduled',
                ignoreschedulablepermission: params.ignoreSchedulablePermission !== false, // Default to true
                nopatientcase: params.noPatientCase || false
            };

            console.log('Rescheduling appointment:', params.appointmentId, 'to', params.newAppointmentId, 'with params:', requestParams);
            const response = await this.makeRequest(endpoint, 'PUT', requestParams);
            return response;
        } catch (error) {
            console.error('Failed to reschedule appointment:', error);
            throw new Error('Failed to reschedule appointment: ' + (error.response?.data?.error || error.message));
        }
    }

    /**
     * Completes the check-out process for a given appointment
     * 
     * @param {number} appointmentId - The ID of the appointment to check out
     * @returns {Promise<Object>} - API response with status of the operation
     */
    async checkoutAppointment(appointmentId) {
      if (!appointmentId) {
        throw new Error('Appointment ID is required to check out an appointment');
      }

      try {
        const endpoint = `appointments/${appointmentId}/checkout`;
        
        console.log('Checking out appointment:', appointmentId);
        const response = await this.makeRequest(endpoint, 'POST', {});
        
        if (response && response.success) {
          console.log('Successfully checked out appointment:', appointmentId);
        } else {
          console.warn('Checkout API returned success=false:', response);
        }
        
        return response;
      } catch (error) {
        console.error('Failed to check out appointment:', error);
        throw new Error('Failed to check out appointment: ' + (error.response?.data?.error || error.message));
      }
    }

    /**
     * Completes the check-in process for a given appointment
     * 
     * @param {number} appointmentId - The ID of the appointment to check in
     * @returns {Promise<Object>} - API response with status of the operation
     */
    async checkinAppointment(appointmentId) {
      if (!appointmentId) {
        throw new Error('Appointment ID is required to check in an appointment');
      }

      try {
        const endpoint = `appointments/${appointmentId}/checkin`;
        
        console.log('Checking in appointment:', appointmentId);
        const response = await this.makeRequest(endpoint, 'POST', {});
        
        if (response && response.success) {
          console.log('Successfully checked in appointment:', appointmentId);
        } else {
          console.warn('Check-in API returned success=false:', response);
        }
        
        return response;
      } catch (error) {
        console.error('Failed to check in appointment:', error);
        throw new Error('Failed to check in appointment: ' + (error.response?.data?.error || error.message));
      }
    }

    /**
     * Create insurance for a patient
     * 
     * @param {number} patientId - The ID of the patient
     * @param {Object} insuranceData - Insurance data for the patient
     * @param {number} insuranceData.insurancepackageid - The athenaNet insurance package ID
     * @param {string} insuranceData.insuranceidnumber - The insurance policy ID number
     * @param {number} insuranceData.sequencenumber - 1 for primary, 2 for secondary
     * @param {string} insuranceData.policynumber - The insurance group number
     * @param {string} insuranceData.insurancepolicyholdersex - The sex of the policy holder (M/F)
     * @param {string} insuranceData.insurancepolicyholderfirstname - First name of the policy holder
     * @param {string} insuranceData.insurancepolicyholderlastname - Last name of the policy holder
     * @param {number} [insuranceData.relationshiptoinsuredid=1] - Relationship to insured ID (1=self)
     * @returns {Promise<Object>} - API response with the created insurance
     */
    async createPatientInsurance(patientId, insuranceData) {
        if (!patientId) {
            throw new Error('Patient ID is required to create insurance');
        }
        
        // Check if insurancepackageid is provided (check for undefined/null but allow 0)
        if (insuranceData.insurancepackageid === undefined || insuranceData.insurancepackageid === null) {
            throw new Error('Insurance package ID is required');
        }
        
        try {
            const endpoint = `patients/${patientId}/insurances`;
            const params = {
                insurancepackageid: insuranceData.insurancepackageid,
                insuranceidnumber: insuranceData.insuranceidnumber || '',
                sequencenumber: insuranceData.sequencenumber || 1,
                policynumber: insuranceData.policynumber || '',
                insurancepolicyholdersex: insuranceData.insurancepolicyholdersex || '',
                insurancepolicyholderfirstname: insuranceData.insurancepolicyholderfirstname || '',
                insurancepolicyholderlastname: insuranceData.insurancepolicyholderlastname || '',
                relationshiptoinsuredid: insuranceData.relationshiptoinsuredid || 1,
                insurancepolicyholderdob: insuranceData.insurancepolicyholderdob || ''
            };
            
            console.log('Creating patient insurance:', params);
            const response = await this.makeRequest(endpoint, 'POST', params);
            
            return response;
        } catch (error) {
            console.error('Failed to create patient insurance:', error);
            throw new Error('Failed to create patient insurance: ' + (error.response?.data?.error || error.message));
        }
    }

    /**
     * Updates information for a specific provider
     * 
     * @param {number} practiceId - The practice ID
     * @param {number} providerId - The provider ID to update
     * @param {Object} providerData - The data to update
     * @returns {Promise<Object>} - Response from the update API
     */
    async updateProvider(practiceId, providerId, providerData) {
        try {
            console.log(`Updating provider with ID ${providerId}:`, providerData);
            
            // Build parameters from providerData
            const params = { ...providerData };
            
            console.log('Formatted provider data for update API:', params);
            const endpoint = `providers/${providerId}`;
            const response = await this.makeRequest(endpoint, 'PUT', params);
            console.log('Provider update response:', response);
            
            return response;
        } catch (error) {
            console.error('updateProvider error:', error);
            throw new Error('Failed to update provider: ' + (error.response?.data?.error || error.message));
        }
    }
}

export default new AthenaService();
